import { useTranslation } from "react-i18next"

import type { FairUseEntitlementFormRow } from "./fair-use-entitlements-utils"
import type { FairUseConfig, FairUseEntitlementSchema, FairUseTier } from "@/api/admin/fair-use"

import { RefreshCw } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { FairUseEntitlementsMatrix } from "./fair-use-entitlements-matrix"
import {
  fairUseEntitlementIssues,
  fairUseHasEntitlements,
  fairUseVoicePricesIssue,
} from "./fair-use-entitlements-utils"
import { FairUseImagePrices } from "./fair-use-image-prices"
import {
  fairUseHasImagePrices,
  fairUseHasVoiceTokenPrices,
  fairUseImagePricesIssue,
  fairUseVoiceTokenPricesIssue,
} from "./fair-use-pricing-utils"
import {
  fairUsePlansIssue,
  fairUsePlansPanelIsSaveable,
  formatFairUseSek,
  readFairUsePlanAmounts,
} from "./fair-use-settings-utils"
import { type FairUseConfigFormState } from "./fair-use-utils"
import { FairUseVoiceRates } from "./fair-use-voice-rates"
import { FairUseVoiceTokenPrices } from "./fair-use-voice-token-prices"

type FairUsePlansPanelProps = {
  form: FairUseConfigFormState
  /** The stored config, for the read-only tenant summary. */
  config: FairUseConfig
  /** Bounds + effort ladder for the matrix, when the platform sent them. */
  entitlementSchema?: FairUseEntitlementSchema
  /** Mirrors `PLATFORM_ONLY_CONFIG_FIELDS` on the backend. */
  canEdit: boolean
  isSaving: boolean
  isDirty: boolean
  onChange: (patch: Partial<FairUseConfigFormState>) => void
  onSave: () => void
}

type PlanFieldProps = {
  caption: string
  id: string
  name: string
  value: string
  badge?: string
  onChange: (value: string) => void
}

function FairUsePlanCard({ badge, caption, id, name, value, onChange }: PlanFieldProps) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-semibold" htmlFor={id}>
          {name}
        </Label>
        {badge ? <Badge variant="secondary">{badge}</Badge> : null}
      </div>
      <div className="flex items-baseline gap-2">
        <Input
          className="h-10 w-24 tabular-nums"
          id={id}
          inputMode="decimal"
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <span className="text-muted-foreground text-xs">
          {t("adminDashboard.fairUse.settings.plans.perMonth", "SEK / month")}
        </span>
      </div>
      <p className="text-muted-foreground text-xs">{caption}</p>
    </div>
  )
}

/**
 * The commercial envelope: what each plan is worth per month, and what a
 * top-up adds and costs.
 *
 * These are the INPUTS the invoice is computed from — the top-up price is
 * stamped onto every grant and summed into `grant_revenue_sek`, and the
 * monthly budgets decide whether a billing event is produced at all — so the
 * backend accepts them from platform admins only
 * (`PLATFORM_ONLY_CONFIG_FIELDS`, 403 `platform_admin_required` otherwise).
 *
 * A tenant admin gets a READ-ONLY SUMMARY rather than five disabled inputs
 * with a padlock. Disabled boxes invite an edit that cannot happen and say
 * nothing about who can make it; one sentence of prose answers both questions.
 * The numbers in it are the tenant's real ones — the config GET is gated on
 * the `fair_use` capability and returns the whole document, commercial fields
 * included, so there is nothing to guess at or hide behind an em dash.
 *
 * `overrides` — the per-user plan map — is on neither path. It is written one
 * oid at a time through `PUT /api/admin/fair-use/users/<oid>/tier` from the
 * AI-budget table; see {@link fairUsePlansUpdate}.
 */
export function FairUsePlansPanel({
  form,
  config,
  entitlementSchema,
  canEdit,
  isSaving,
  isDirty,
  onChange,
  onSave,
}: FairUsePlansPanelProps) {
  const { t, i18n } = useTranslation()

  // The round-2 block is rendered from what the backend ACTUALLY sent. A
  // round-1 deployment has no `entitlements` at all, and a matrix drawn from
  // the shipped defaults would state limits this tenant is not on — worse than
  // no matrix. The same flag decides what the Save may put on the wire.
  const hasEntitlements = fairUseHasEntitlements(config)

  // Round 3 ships two more price tables, each its own `_VALID_CONFIG_FIELDS`
  // entry. Gated on what the backend ACTUALLY sent, one field at a time, for
  // the same reason the matrix is: a table rendered against a backend that
  // does not know the field saves as 400 `invalid_config` and applies nothing.
  const hasImagePrices = fairUseHasImagePrices(config)
  const hasVoiceTokenPrices = fairUseHasVoiceTokenPrices(config)

  const entitlementIssues = hasEntitlements
    ? fairUseEntitlementIssues(form.entitlements, entitlementSchema)
    : []
  const voiceIssue = hasEntitlements ? fairUseVoicePricesIssue(form.voicePrices) : null
  const imageIssue = hasImagePrices ? fairUseImagePricesIssue(form.imagePrices) : null
  const voiceTokenIssue = hasVoiceTokenPrices
    ? fairUseVoiceTokenPricesIssue(form.voiceTokenPrices)
    : null

  const handleEntitlementChange = (
    plan: FairUseTier,
    patch: Partial<FairUseEntitlementFormRow>
  ) => {
    onChange({
      entitlements: { ...form.entitlements, [plan]: { ...form.entitlements[plan], ...patch } },
    })
  }

  const round2Block = hasEntitlements ? (
    <div className="space-y-6 border-t pt-5">
      <FairUseEntitlementsMatrix
        canEdit={canEdit}
        form={form.entitlements}
        issues={entitlementIssues}
        schema={entitlementSchema}
        onChange={handleEntitlementChange}
      />
      <FairUseVoiceRates
        canEdit={canEdit}
        issue={voiceIssue}
        rows={form.voicePrices}
        onChange={(voicePrices) => onChange({ voicePrices })}
      />
    </div>
  ) : null

  const round3Block =
    hasImagePrices || hasVoiceTokenPrices ? (
      <div className="space-y-6 border-t pt-5">
        {hasImagePrices ? (
          <FairUseImagePrices
            canEdit={canEdit}
            issue={imageIssue}
            rows={form.imagePrices}
            onChange={(imagePrices) => onChange({ imagePrices })}
          />
        ) : null}
        {hasVoiceTokenPrices ? (
          <FairUseVoiceTokenPrices
            canEdit={canEdit}
            issue={voiceTokenIssue}
            rows={form.voiceTokenPrices}
            onChange={(voiceTokenPrices) => onChange({ voiceTokenPrices })}
          />
        ) : null}
      </div>
    ) : null

  if (!canEdit) {
    const amounts = readFairUsePlanAmounts(config)
    const sek = (value: number) => formatFairUseSek(value, i18n.language)
    return (
      <div className="space-y-2" data-testid="fair-use-plans-readonly">
        <div className="bg-muted/40 space-y-1 rounded-lg border border-dashed p-4">
          <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            {t("adminDashboard.fairUse.settings.plans.readOnlyTitle", "What this tenant is on")}
          </p>
          <p className="text-sm">
            {t("adminDashboard.fairUse.settings.plans.readOnlySummary", {
              defaultValue:
                "Standard {{standard}} SEK · Pro {{pro}} SEK · Unlimited {{unlimited}} SEK · Top-up {{topUpSize}} SEK for {{topUpPrice}} SEK",
              pro: sek(amounts.pro_monthly_cap_sek),
              standard: sek(amounts.default_monthly_cap_sek),
              topUpPrice: sek(amounts.block_list_price_sek),
              topUpSize: sek(amounts.block_cap_sek),
              unlimited: sek(amounts.unlimited_monthly_cap_sek),
            })}
          </p>
          <p className="text-muted-foreground text-xs">
            {t(
              "adminDashboard.fairUse.settings.plans.readOnlyHint",
              "Changes to plans and prices go through your platform provider."
            )}
          </p>
        </div>
        {round2Block}
        {round3Block}
      </div>
    )
  }

  const issue = fairUsePlansIssue(form)
  const canSave =
    isDirty &&
    !isSaving &&
    fairUsePlansPanelIsSaveable(form, {
      includeEntitlements: hasEntitlements,
      includeImagePrices: hasImagePrices,
      includeVoiceTokenPrices: hasVoiceTokenPrices,
      schema: entitlementSchema,
    })

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-muted-foreground max-w-prose text-sm">
          {t(
            "adminDashboard.fairUse.settings.plans.caption",
            "Set by your platform provider. Tenant admins see them read-only."
          )}
        </p>
        <Button disabled={!canSave} onClick={onSave}>
          {isSaving ? <RefreshCw className="mr-2 size-4 animate-spin" /> : null}
          {t("adminDashboard.fairUse.settings.save", "Save")}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <FairUsePlanCard
          badge={t("adminDashboard.fairUse.settings.plans.standardBadge", "Default")}
          id="fair-use-plan-standard"
          name={t("adminDashboard.fairUse.plans.standard", "Standard")}
          value={form.defaultMonthlyCapSek}
          caption={t(
            "adminDashboard.fairUse.settings.plans.standardCaption",
            "Everyone starts here."
          )}
          onChange={(defaultMonthlyCapSek) => onChange({ defaultMonthlyCapSek })}
        />
        <FairUsePlanCard
          id="fair-use-plan-pro"
          name={t("adminDashboard.fairUse.plans.pro", "Pro")}
          value={form.proMonthlyCapSek}
          caption={t(
            "adminDashboard.fairUse.settings.plans.proCaption",
            "Assign per user in Usage & cost."
          )}
          onChange={(proMonthlyCapSek) => onChange({ proMonthlyCapSek })}
        />
        <FairUsePlanCard
          id="fair-use-plan-unlimited"
          name={t("adminDashboard.fairUse.plans.unlimited", "Unlimited")}
          value={form.unlimitedMonthlyCapSek}
          caption={t(
            "adminDashboard.fairUse.settings.plans.unlimitedCaption",
            "Tracked, warned, never stopped."
          )}
          onChange={(unlimitedMonthlyCapSek) => onChange({ unlimitedMonthlyCapSek })}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="fair-use-top-up-size">
            {t("adminDashboard.fairUse.settings.plans.topUpSizeLabel", "Top-up size")}
          </Label>
          <div className="flex items-center gap-2">
            <Input
              className="h-10 w-28 tabular-nums"
              id="fair-use-top-up-size"
              inputMode="decimal"
              type="text"
              value={form.blockCapSek}
              onChange={(event) => onChange({ blockCapSek: event.target.value })}
            />
            <span className="text-muted-foreground text-xs">
              {t("adminDashboard.fairUse.settings.plans.topUpSizeHelp", "SEK of budget per top-up")}
            </span>
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="fair-use-top-up-price">
            {t("adminDashboard.fairUse.settings.plans.topUpPriceLabel", "Top-up price (billed)")}
          </Label>
          <div className="flex items-center gap-2">
            <Input
              className="h-10 w-28 tabular-nums"
              id="fair-use-top-up-price"
              inputMode="decimal"
              type="text"
              value={form.blockListPriceSek}
              onChange={(event) => onChange({ blockListPriceSek: event.target.value })}
            />
            <span className="text-muted-foreground text-xs">
              {t(
                "adminDashboard.fairUse.settings.plans.topUpPriceHelp",
                "SEK list price on the invoice"
              )}
            </span>
          </div>
        </div>
      </div>

      {issue === "order" && (
        <p className="text-destructive text-xs" data-testid="fair-use-plans-order-error">
          {t(
            "adminDashboard.fairUse.settings.plans.orderError",
            "Monthly budgets must rise: Standard ≤ Pro ≤ Unlimited."
          )}
        </p>
      )}
      {issue === "range" && (
        <p className="text-destructive text-xs" data-testid="fair-use-plans-range-error">
          {t(
            "adminDashboard.fairUse.settings.plans.rangeError",
            "Every amount must be greater than 0 — only the top-up price may be 0. Write decimals with a comma or a point (1,50) and no thousands grouping."
          )}
        </p>
      )}

      {round2Block}
      {round3Block}
    </div>
  )
}
